import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  User, 
  Phone, 
  EnvelopeSimple, 
  MapPin, 
  Calendar, 
  TrendUp, 
  Activity,
  FileText,
  Bed,
  Clock,
  CheckCircle,
  Image as ImageIcon
} from '@phosphor-icons/react';
import api from '../utils/api';
import { toast } from 'sonner';
import BMIDisplay from './BMIDisplay';

const CATEGORY_COLORS = {
  'New Customer': 'bg-blue-100 text-blue-800',
  'New': 'bg-green-100 text-green-800',
  '10 Days': 'bg-yellow-100 text-yellow-800',
  '30 Days': 'bg-orange-100 text-orange-800',
  '60 Days': 'bg-red-100 text-red-800',
  '90 Days': 'bg-purple-100 text-purple-800',
  '120 Days': 'bg-pink-100 text-pink-800',
  'Buyers': 'bg-emerald-100 text-emerald-800'
};

const CustomerDetailModal = ({ open, onClose, customerId }) => {
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);

  useEffect(() => {
    if (open && customerId) {
      fetchCustomerDetails();
    }
  }, [open, customerId]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/customers/${customerId}/complete-profile`);
      setCustomerData(response.data);
    } catch (error) {
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  if (!customerId) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white w-[95vw] max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-semibold text-[#2C4C3B] flex items-center gap-2 sm:gap-3">
            <User size={32} weight="fill" />
            Complete Customer Profile
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-lg text-[#1C1C1A]/60">Loading customer data...</p>
          </div>
        ) : customerData ? (
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="sessions">Session Notes</TabsTrigger>
              <TabsTrigger value="attendance">Attendance</TabsTrigger>
              <TabsTrigger value="reports">Reports</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4 mt-4">
              {/* Personal Information */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-[#2C4C3B] mb-4">Personal Information</h3>
                
                <div className="flex flex-col md:flex-row gap-6 mb-6">
                  {/* Customer Photo */}
                  <div className="flex-shrink-0">
                    {customerData.customer.photo ? (
                      <img
                        src={customerData.customer.photo}
                        alt={customerData.customer.name}
                        className="w-32 h-32 rounded-lg object-cover border-2 border-[#2C4C3B]/20 shadow-sm"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-lg bg-[#E2E8E4] border-2 border-[#2C4C3B]/20 flex items-center justify-center">
                        <div className="text-center">
                          <User size={48} className="text-[#2C4C3B]/40 mx-auto mb-2" weight="fill" />
                          <p className="text-xs text-[#1C1C1A]/40">No Photo</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Customer Details */}
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <User size={20} className="text-[#2C4C3B]" />
                      <div>
                        <p className="text-xs text-[#1C1C1A]/60">Full Name</p>
                        <p className="font-medium">{customerData.customer.name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-[#2C4C3B]" />
                      <div>
                        <p className="text-xs text-[#1C1C1A]/60">Registration No</p>
                        <p className="font-medium">{customerData.customer.reg_no}</p>
                      </div>
                    </div>

                  <div className="flex items-center gap-3">
                    <Phone size={20} className="text-[#2C4C3B]" />
                    <div>
                      <p className="text-xs text-[#1C1C1A]/60">Phone Number</p>
                      <p className="font-medium">{customerData.customer.phone_number}</p>
                    </div>
                  </div>

                  {customerData.customer.email && (
                    <div className="flex items-center gap-3">
                      <EnvelopeSimple size={20} className="text-[#2C4C3B]" />
                      <div>
                        <p className="text-xs text-[#1C1C1A]/60">Email</p>
                        <p className="font-medium">{customerData.customer.email}</p>
                      </div>
                    </div>
                  )}

                  {customerData.customer.age && (
                    <div className="flex items-center gap-3">
                      <Calendar size={20} className="text-[#2C4C3B]" />
                      <div>
                        <p className="text-xs text-[#1C1C1A]/60">Age</p>
                        <p className="font-medium">{customerData.customer.age} years</p>
                      </div>
                    </div>
                  )}

                  {customerData.customer.address && (
                    <div className="flex items-center gap-3">
                      <MapPin size={20} className="text-[#2C4C3B]" />
                      <div>
                        <p className="text-xs text-[#1C1C1A]/60">Address</p>
                        <p className="font-medium">{customerData.customer.address}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Calendar size={20} className="text-[#2C4C3B]" />
                    <div>
                      <p className="text-xs text-[#1C1C1A]/60">Registration Date</p>
                      <p className="font-medium">
                        {new Date(customerData.customer.created_at).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Badge className={CATEGORY_COLORS[customerData.customer.customer_category] || 'bg-gray-100'}>
                      {customerData.customer.customer_category}
                    </Badge>
                  </div>
                  </div>
                </div>

                {customerData.customer.problem && (
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm font-medium text-orange-800 mb-1">Problem/Condition:</p>
                    <p className="text-sm text-[#1C1C1A]/80">{customerData.customer.problem}</p>
                  </div>
                )}
              </Card>

              {/* Session Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={32} className="text-blue-600" weight="fill" />
                    <div>
                      <p className="text-sm text-blue-600">Sessions Completed</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {customerData.customer.sessions_completed}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
                  <div className="flex items-center gap-3">
                    <TrendUp size={32} className="text-green-600" weight="fill" />
                    <div>
                      <p className="text-sm text-green-600">Avg Effectiveness</p>
                      <p className="text-2xl font-bold text-green-900">
                        {customerData.avg_effectiveness}%
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100">
                  <div className="flex items-center gap-3">
                    <FileText size={32} className="text-purple-600" weight="fill" />
                    <div>
                      <p className="text-sm text-purple-600">Total Notes</p>
                      <p className="text-2xl font-bold text-purple-900">
                        {customerData.session_notes.length}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Category Timeline */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-[#2C4C3B] mb-4">Category Progression</h3>
                {customerData.category_timeline && (
                  <div className="flex flex-wrap gap-2">
                    {customerData.category_timeline.timeline.map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Card className={`p-3 ${CATEGORY_COLORS[entry.category] || 'bg-gray-50'}`}>
                          <div className="text-sm font-medium">{entry.category}</div>
                          <div className="text-xs opacity-75">{entry.session_count} sessions</div>
                          <div className="text-xs opacity-60">{entry.start_date}</div>
                        </Card>
                        {idx < customerData.category_timeline.timeline.length - 1 && (
                          <span className="text-[#1C1C1A]/40">→</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Staff Members Who Attended */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-[#2C4C3B] mb-4">Staff Members</h3>
                <div className="flex flex-wrap gap-2">
                  {customerData.attended_by_staff.map((staff, idx) => (
                    <Badge key={idx} variant="outline" className="text-sm">
                      {staff.name} ({staff.count} sessions)
                    </Badge>
                  ))}
                </div>
              </Card>
            </TabsContent>

            {/* Session Notes Tab */}
            <TabsContent value="sessions" className="space-y-4 mt-4">
              {customerData.session_notes.length === 0 ? (
                <Card className="p-12 text-center">
                  <FileText size={48} className="mx-auto mb-4 text-gray-400" />
                  <p className="text-lg text-[#1C1C1A]/60">No session notes yet</p>
                </Card>
              ) : (
                customerData.session_notes.map((note) => (
                  <Card key={note.note_id} className="p-4 border-l-4 border-[#2C4C3B]">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-[#2C4C3B]" />
                        <span className="font-medium">{note.session_date}</span>
                        {note.batch_time && (
                          <Badge variant="outline" className="text-xs">
                            {note.batch_time}
                          </Badge>
                        )}
                      </div>
                      <Badge className="bg-green-100 text-green-800">
                        {note.treatment_effectiveness}% effective
                      </Badge>
                    </div>

                    <p className="text-sm text-[#1C1C1A] mb-3">{note.session_notes}</p>

                    {/* BMI Data Display */}
                    {note.bmi_data && (
                      <BMIDisplay bmiData={note.bmi_data} />
                    )}

                    {note.improvement_action_notes && (
                      <div className="mb-2 p-2 bg-blue-50 rounded text-xs">
                        <span className="font-medium text-blue-800">Action: </span>
                        <span>{note.improvement_action_notes}</span>
                      </div>
                    )}

                    {note.improvement_result_notes && (
                      <div className="mb-2 p-2 bg-green-50 rounded text-xs">
                        <span className="font-medium text-green-800">Result: </span>
                        <span>{note.improvement_result_notes}</span>
                      </div>
                    )}

                    {note.therapy_list && note.therapy_list.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-[#1C1C1A]/60 mb-1">Therapies:</p>
                        <div className="flex flex-wrap gap-1">
                          {note.therapy_list.map((therapy, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {therapy}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-[#1C1C1A]/50 mt-3 pt-2 border-t">
                      By {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                    </div>
                  </Card>
                ))
              )}
            </TabsContent>

            {/* Attendance History Tab */}
            <TabsContent value="attendance" className="space-y-4 mt-4">
              {customerData.attendance_history.length === 0 ? (
                <Card className="p-12 text-center">
                  <CheckCircle size={48} className="mx-auto mb-4 text-gray-400" />
                  <p className="text-lg text-[#1C1C1A]/60">No attendance records</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {customerData.attendance_history.map((record, idx) => (
                    <Card key={idx} className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-[#2C4C3B]">{record.date}</p>
                          <p className="text-xs text-[#1C1C1A]/60">
                            Token #{record.token_number}
                          </p>
                        </div>
                        <Badge className={CATEGORY_COLORS[record.customer_category] || 'bg-gray-100'}>
                          {record.customer_category}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span>{record.allocated_batch_time}</span>
                        </div>
                        {record.bed_number && (
                          <div className="flex items-center gap-1">
                            <Bed size={16} />
                            <span>Bed #{record.bed_number}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-2 flex items-center justify-between">
                        <Badge variant={record.completed ? 'default' : 'outline'}>
                          {record.completed ? 'Completed' : record.status}
                        </Badge>
                        {record.attended_by && (
                          <div className="text-xs text-[#1C1C1A]/60">
                            <span className="font-medium">Attended by:</span> {record.attended_by}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports" className="space-y-4 mt-4">
              {customerData.session_notes.filter(n => n.report_image).length === 0 ? (
                <Card className="p-12 text-center">
                  <ImageIcon size={48} className="mx-auto mb-4 text-gray-400" />
                  <p className="text-lg text-[#1C1C1A]/60">No report images uploaded</p>
                </Card>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {customerData.session_notes
                    .filter(n => n.report_image)
                    .map((note) => (
                      <Card key={note.note_id} className="p-2">
                        <img
                          src={note.report_image.startsWith('data:') ? note.report_image : `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${note.report_image}`}
                          alt="Report"
                          className="w-full h-40 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Create a new window with the image
                            const win = window.open();
                            const imageUrl = note.report_image.startsWith('data:') ? note.report_image : `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${note.report_image}`;
                            win.document.write(`
                              <html>
                                <head>
                                  <title>Report Image - ${note.session_date}</title>
                                  <style>
                                    body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #000; }
                                    img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                                  </style>
                                </head>
                                <body>
                                  <img src="${imageUrl}" alt="Report Image" />
                                </body>
                              </html>
                            `);
                            win.document.close();
                          }}
                        />
                        <p className="text-xs text-[#1C1C1A]/60 mt-2">{note.session_date}</p>
                      </Card>
                    ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerDetailModal;
